import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Replay as ReplayIcon } from '@mui/icons-material';
import axios from 'axios';
import { harnessService } from '@/services/api/harnessService';
import type { BatchDetailDto, MeasurementDto } from '@/features/harness/types';
import logger from '@/lib/logger';
import { MeasurementListView } from './MeasurementListView';
import { MeasurementPlotView } from './MeasurementPlotView';
import { CurveParametersView } from './CurveParametersView';
import { ExcitationProfileView } from './ExcitationProfileView';
import { MeasurementAnnotationPanel } from './MeasurementAnnotationPanel';
import { CommunicationLogView } from './CommunicationLogView';
import { ExportButton } from './ExportButton';

export interface BatchReviewProps {
  /** The Batch to open for connection-free review (Story 3.1). */
  batchId: string;
}

/** Re-derive a selection from the SequenceNumber-ordered Measurements so it always reads in run
 *  order regardless of click order (mirrors the live store's `toggleMeasurementSelection`). */
function deriveOrderedSelection(measurements: MeasurementDto[], ids: Set<string>): string[] {
  return measurements.filter((m) => ids.has(m.id)).map((m) => m.id);
}

/**
 * Read-only review of a past Batch (Story 3.1, AC2/AC3/AC4). Sources everything from REST fetches
 * (`getBatch` + `getCommunicationLog`) and reuses the Epic 1 presentational components — exactly as
 * `MeasurementAnalysisPage` reuses the chart components with data from a query rather than live
 * state. It never reads `useHarnessStore` and never touches `DeviceService`: reviewing a Batch is
 * entirely connection-free. Selection (multi-select overlay + comparison table, Story 2.1) works
 * over the fetched Measurements via local state. Adding further Measurements is not possible here —
 * that requires Resume (Story 1.5), which reconnects the matching device; a Resume affordance
 * routes there.
 */
export function BatchReview({ batchId }: BatchReviewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [outcomeReport, setOutcomeReport] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [isSavingOutcomeReport, setIsSavingOutcomeReport] = useState(false);
  const [outcomeReportError, setOutcomeReportError] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['harness-batch', batchId],
    queryFn: () => harnessService.getBatch(batchId),
  });

  const measurements = data?.measurements ?? [];

  // Default the selection to every Measurement once the Batch loads, so opening a past Batch
  // immediately overlays all its curves (parity with the Analysis tool) — but seed only once per
  // Batch, not on every `data` update. react-query may deliver a fresh `data` object for the same
  // batchId (a background refetch — e.g. window refocus, or another session adding a Measurement
  // to this Batch concurrently) without an unmount in between; without this guard, a naive
  // effect keyed on `data` would re-fire then and silently blow away the user's show/exclude
  // edits back to "select all". The same guard protects the Description/Outcome Report fields
  // below — without it, a background refetch of the same Batch would silently clobber an
  // in-progress, unsaved edit. (BatchReviewPage's `key={batchId}` already forces a fresh mount,
  // and thus a fresh ref, on an actual batch-to-batch navigation.)
  const seededBatchRef = useRef<string | null>(null);
  // The last value we seeded or successfully saved for each field. onBlur compares against these
  // (NOT against live query data, which a concurrent refetch may have advanced) to (a) skip a
  // redundant PATCH when nothing changed and (b) avoid clobbering a concurrent session's newer
  // value with a stale locally-seeded one on a no-op blur.
  const savedNameRef = useRef('');
  const savedDescriptionRef = useRef('');
  const savedOutcomeReportRef = useRef('');
  useEffect(() => {
    if (data && seededBatchRef.current !== batchId) {
      seededBatchRef.current = batchId;
      setSelectedIds(data.measurements.map((m) => m.id));
      const seededName = data.batch.name ?? '';
      const seededDescription = data.batch.description ?? '';
      const seededOutcomeReport = data.batch.outcomeReport ?? '';
      setName(seededName);
      setDescription(seededDescription);
      setOutcomeReport(seededOutcomeReport);
      savedNameRef.current = seededName;
      savedDescriptionRef.current = seededDescription;
      savedOutcomeReportRef.current = seededOutcomeReport;
    }
  }, [data, batchId]);

  const handleNameBlur = async () => {
    if (!data) return;
    if (name === savedNameRef.current) return;
    setIsSavingName(true);
    setNameError(false);
    try {
      await harnessService.updateBatchMetadata(data.batch.id, { name });
      savedNameRef.current = name;
      queryClient.setQueryData<BatchDetailDto>(['harness-batch', batchId], (old) =>
        old ? { ...old, batch: { ...old.batch, name } } : old,
      );
    } catch (err) {
      logger.error(`Failed to save Name for Batch ${data.batch.id}:`, err);
      setNameError(true);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleDescriptionBlur = async () => {
    if (!data) return;
    // Skip a redundant PATCH when the field is unchanged since it was seeded/last saved — a bare
    // focus-then-blur (no typing) must not issue a write, and must not clobber a concurrent
    // session's newer value with our stale seed.
    if (description === savedDescriptionRef.current) return;
    setIsSavingDescription(true);
    setDescriptionError(false);
    try {
      await harnessService.updateBatchMetadata(data.batch.id, { description });
      savedDescriptionRef.current = description;
      // Write the saved value into the react-query cache so a same-batch remount (BatchReviewPage's
      // key={batchId}) re-seeds the just-saved edit rather than a stale pre-save value.
      queryClient.setQueryData<BatchDetailDto>(['harness-batch', batchId], (old) =>
        old ? { ...old, batch: { ...old.batch, description } } : old,
      );
    } catch (err) {
      logger.error(`Failed to save Description for Batch ${data.batch.id}:`, err);
      setDescriptionError(true);
    } finally {
      setIsSavingDescription(false);
    }
  };

  const handleOutcomeReportBlur = async () => {
    if (!data) return;
    if (outcomeReport === savedOutcomeReportRef.current) return;
    setIsSavingOutcomeReport(true);
    setOutcomeReportError(false);
    try {
      await harnessService.updateBatchMetadata(data.batch.id, { outcomeReport });
      savedOutcomeReportRef.current = outcomeReport;
      queryClient.setQueryData<BatchDetailDto>(['harness-batch', batchId], (old) =>
        old ? { ...old, batch: { ...old.batch, outcomeReport } } : old,
      );
    } catch (err) {
      logger.error(`Failed to save Outcome Report for Batch ${data.batch.id}:`, err);
      setOutcomeReportError(true);
    } finally {
      setIsSavingOutcomeReport(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return deriveOrderedSelection(measurements, next);
    });
  };

  const selectAll = () => setSelectedIds(measurements.map((m) => m.id));
  const clearSelection = () => setSelectedIds([]);

  // A malformed/stale Batch ID in the URL (the most realistic way to land on a bad one — hand-edited
  // or a stale bookmark) fails ASP.NET's Guid model binding with a 400, not a 404 — distinguish it
  // with the same wording BatchControls' resume flow already uses for the identical case, rather
  // than falling through to the generic error message.
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const isNotFound = status === 404;
  const isBadId = status === 400;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/harness/batches')} sx={{ mb: 2 }}>
        Back to Batches
      </Button>

      {isLoading && <CircularProgress size={24} />}

      {error && (
        <Alert severity={isNotFound || isBadId ? 'warning' : 'error'}>
          {isNotFound
            ? 'Batch not found.'
            : isBadId
              ? 'That is not a valid Batch ID.'
              : 'Failed to load this Batch. Please try again.'}
        </Alert>
      )}

      {data && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" gutterBottom>
              Batch Review
            </Typography>
            <ExportButton batchId={data.batch.id} />
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {data.batch.deviceUid && <Chip size="small" label={`Device: ${data.batch.deviceUid}`} />}
            {data.batch.serialNumber && <Chip size="small" label={`Serial: ${data.batch.serialNumber}`} />}
            {data.batch.firmwareVersion && <Chip size="small" label={`FW: ${data.batch.firmwareVersion}`} />}
            {data.batch.hardwareVersion && <Chip size="small" label={`HW: ${data.batch.hardwareVersion}`} />}
          </Stack>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Recorded {new Date(data.batch.createdAt).toLocaleString()} — Batch ID {data.batch.id}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              disabled={isSavingName}
              inputProps={{ maxLength: 200 }}
            />
            {nameError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Failed to save Name. Please try again.
              </Alert>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Description"
              multiline
              rows={3}
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescriptionBlur}
              disabled={isSavingDescription}
            />
            {descriptionError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Failed to save Description. Please try again.
              </Alert>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <TextField
              label="Outcome Report"
              multiline
              rows={3}
              fullWidth
              size="small"
              value={outcomeReport}
              onChange={(e) => setOutcomeReport(e.target.value)}
              onBlur={handleOutcomeReportBlur}
              disabled={isSavingOutcomeReport}
            />
            {outcomeReportError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Failed to save Outcome Report. Please try again.
              </Alert>
            )}
          </Box>

          <Alert severity="info" icon={<ReplayIcon />} sx={{ mt: 2 }}>
            Reviewing a Batch never requires a device. To add further Measurements, resume this Batch
            — that reconnects the matching device.
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ReplayIcon />}
                onClick={() => navigate(`/harness?resume=${encodeURIComponent(batchId)}`)}
              >
                Add Measurements (Resume)
              </Button>
            </Box>
          </Alert>

          <Divider sx={{ my: 2 }} />

          <MeasurementListView
            measurements={measurements}
            selectedIds={selectedIds}
            onToggleSelection={toggleSelection}
            onSelectAll={selectAll}
            onClearSelection={clearSelection}
          />
          <MeasurementPlotView measurements={measurements} selectedIds={selectedIds} />
          <CurveParametersView batchId={data.batch.id} selectedIds={selectedIds} />
          <ExcitationProfileView measurements={measurements} selectedIds={selectedIds} />
          <MeasurementAnnotationPanel
            batchId={data.batch.id}
            selectedIds={selectedIds}
            measurements={measurements}
            onMeasurementNoteSaved={(measurementId, note) =>
              // Write the persisted Note back into the cached Batch detail so re-selecting this
              // Measurement re-seeds the panel's field from the saved value, not the stale fetch.
              queryClient.setQueryData<BatchDetailDto>(['harness-batch', batchId], (prev) =>
                prev
                  ? {
                      ...prev,
                      measurements: prev.measurements.map((m) =>
                        m.id === measurementId ? { ...m, note } : m,
                      ),
                    }
                  : prev,
              )
            }
          />
          <CommunicationLogView batchId={data.batch.id} />
        </>
      )}
    </Box>
  );
}
